import React, { useEffect, useState } from "react";
import { DatePicker, Modal, Select, TimePicker } from "antd";
import { Button, Form } from "antd";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { ReactComponent as RepeatIcon } from "../../../assets/images/icons/repeat.svg";
import "./AddShiftModal.scss";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ShiftType } from "../../../Pages/Shifts/utils";
import DaySelector from "./Components/DaySelector/DaySelector";
import MyMultiDatePicker from "./Components/MultiDayPicker/MultiDayPicker";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Option } = Select;

export enum TimeFrame {
  WEEK = "week",
  MONTH = "month",
}

type ShiftFormValues = {
  date: any;
  startTime: dayjs.Dayjs;
  endTime: dayjs.Dayjs;
  shiftType: ShiftType;
  timeFrame?: any;
  excludedDates?: any;
  excludedWeekDays?: any;
  isMultipleShift: boolean;
};

type Props = {
  isOpen: boolean;
  onCloseModal: () => void;
  onAddShift: (values: ShiftFormValues) => void;
  shiftModalOpen: any;
};

const AddShiftModal: React.FC<Props> = ({
  isOpen,
  onCloseModal,
  onAddShift,
  shiftModalOpen,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm(); // Create a form instance using Form.useForm()
  const [isMultipleShift, setMultipleShift] = useState<boolean>(false);
  const [excludedRandomDates, setexcludedRandomDates] = useState<Dayjs[]>([]);
  const [excludedWeekDays, setExcludedWeekDays] = useState<number[]>([]);
  useEffect(() => {
    // Set form initial values when activeNotes changes

    // Reset form fields if activeNotes is null or undefined
    form.resetFields();
  }, [form]);
  const handleOk = () => {
    onCloseModal();
  };
  const handleCancel = () => {
    onCloseModal();
  };

  const onFinish = async (values: any) => {
    const { date, startTime, endTime, shiftType, timeFrame } = values;

    const fullStart = dayjs(date)
      .hour(startTime.hour())
      .minute(startTime.minute());

    const fullEnd = dayjs(date).hour(endTime.hour()).minute(endTime.minute());

    onAddShift({
      date: dayjs(date),
      startTime: fullStart,
      endTime: fullEnd,
      shiftType,
      timeFrame,
      excludedDates: excludedRandomDates,
      excludedWeekDays: excludedWeekDays,
      isMultipleShift,
    });
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error("Failed:", errorInfo);
  };

  console.log("shiftModalOpen", shiftModalOpen);

  return (
    <Modal
      title={
        <div className="addShiftHeader">
          <div className="col">
            <div className="profileImg">
              {shiftModalOpen?.user?.avatar?.url ? (
                <img
                  src={shiftModalOpen?.user?.avatar?.url ?? ""}
                  alt="Profile"
                />
              ) : (
                <div className="initials">
                  {`${shiftModalOpen?.user?.firstName?.[0] || ""}${shiftModalOpen?.user?.lastName?.[0] || ""
                    }`}
                </div>
              )}
            </div>
            <div className="info">
              <div className="name">
                {shiftModalOpen?.user?.firstName}{" "}
                {shiftModalOpen?.user?.lastName}
              </div>
              <div className="date">
                {format(shiftModalOpen?.date, "MM.dd.yyyy")}
              </div>
            </div>
          </div>
          <div
            className={`col repeat ${isMultipleShift ? "active" : ""}`}
            onClick={() => setMultipleShift(!isMultipleShift)}
          >
            <RepeatIcon />
          </div>
        </div>
      }
      open={isOpen}
      onOk={handleOk}
      className="addShiftModal"
      onCancel={handleCancel}
      footer={[]}
    >
      <div className="customForm">
        <Form
          name="basic"
          form={form}
          labelCol={{ span: 8 }}
          //   wrapperCol={{ span: 16 }}
          layout="vertical"
          //   style={{ maxWidth: 600 }}
          //   initialValues={{ remember: true }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          onValuesChange={(changedValues, allValues) => {
            const date = allValues.date;

            if (changedValues.startTime && date) {
              const newStart = dayjs(date)
                .hour(changedValues.startTime.hour())
                .minute(changedValues.startTime.minute());
              form.setFieldsValue({ startTime: newStart });
            }

            if (changedValues.endTime && date) {
              const newEnd = dayjs(date)
                .hour(changedValues.endTime.hour())
                .minute(changedValues.endTime.minute());
              form.setFieldsValue({ endTime: newEnd });
            }
          }}
        >
          <div className="shiftModalGlobalItems">
            <Form.Item
              label={t("Date")}
              name="date"
              rules={[{ required: true, message: t("Please select date") }]}
              initialValue={
                shiftModalOpen?.date ? dayjs(shiftModalOpen?.date) : undefined
              }
            >
              <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" placeholder={t("Select date")} />
            </Form.Item>

            <Form.Item
              label={t("Start Time")}
              name="startTime"
              rules={[
                { required: true, message: t("Please select start time") },
              ]}
            >
              <TimePicker format="HH:mm" style={{ width: "100%" }} placeholder={t("Select time")} />
            </Form.Item>

            <Form.Item
              label={t("End Time")}
              name="endTime"
              rules={[{ required: true, message: t("Please select end time") }]}
            >
              <TimePicker format="HH:mm" style={{ width: "100%" }} placeholder={t("Select time")} />
            </Form.Item>

            <Form.Item
              label={t("Shift Type")}
              name="shiftType"
              rules={[
                { required: true, message: t("Please select shift type") },
              ]}
            >
              <Select style={{ width: "100%" }}>
                <Option value={ShiftType.MORNING}>{t("Morning Shift")}</Option>
                <Option value={ShiftType.AFTERNOON}>
                  {t("Afternoon Shift")}
                </Option>
                <Option value={ShiftType.NIGHT}>{t("Night Shift")}</Option>
              </Select>
            </Form.Item>
          </div>
          {isMultipleShift && (
            <div className="multipleShiftItems">
              <>
                <Form.Item label={t("Repeat")} name="repat" rules={[]}>
                  <div className="repeatShift">
                    <DaySelector
                      excludedWeekDays={excludedWeekDays}
                      setExcludedWeekDays={(val) => setExcludedWeekDays(val)}
                    />
                  </div>
                </Form.Item>
              </>
              <MyMultiDatePicker
                date={shiftModalOpen?.date}
                setSelectedDates={(dates: any) => setexcludedRandomDates(dates)}
                selectedDates={excludedRandomDates}
              />
              <Form.Item
                label={t("Time Frame")}
                name="timeFrame"
                rules={[
                  {
                    required: isMultipleShift,
                    message: t("Please select time frame"),
                  },
                ]}
              >
                <Select style={{ width: "100%" }}>
                  <Option value={TimeFrame.WEEK}>{t("Week")}</Option>
                  <Option value={TimeFrame.MONTH}>{t("Month")}</Option>
                </Select>
              </Form.Item>
            </div>
          )}
          <div className="saveShift">
            <Form.Item>
              <Button type="primary" htmlType="submit">
                {t("Save")}
              </Button>
            </Form.Item>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default AddShiftModal;
